import { cancelUpload, uploadFile } from '@weblite/file'
import * as R from 'ramda'
import { Action } from '@weblite/logic-toolkit'
import { MessageType, RawVideoMessage } from '@weblite/ts-action/message'
import { pendingUploadsStorage } from '$logic/pending-uploads/storage'
import {
  PendingUpload,
  PendingUploadVideoAttachment,
  SendMessagePayloadWithPendingId,
} from '$logic/pending-uploads/types'
import { sendMessage } from '$logic/message'
import {
  deleteUploadingFile,
  getUploadingFile,
  saveUploadingFile,
  updateUploadingFile,
} from '$logic/persist-uploads'
import {
  messageTypeToSendingMessageAction,
  onSendingMessageAction,
} from '$logic/message-action'
import { onAfterMessagesSentToChat } from '$logic/opened-room'
import { getThumbnailUploadId } from './helper'
import { createUploadProfiler, UploadSource } from '$logic/profiler'

export function deletePendingUploads(ids: string[]) {
  pendingUploadsStorage.deleteMany(ids)
}

export function deletePendingUpload(uploadId: string) {
  pendingUploadsStorage.delete(uploadId)
  cancelUpload(uploadId)
  cancelUpload(getThumbnailUploadId(uploadId))
  deleteUploadingFile(uploadId)
  deleteUploadingFile(getThumbnailUploadId(uploadId))
  deleteProcessingPendingUploads(uploadId)
}

export async function createPendingUpload(
  message: PendingUpload,
  file: File,
  thumbnailFile?: File,
) {
  if (message.type === 'IMAGE') await saveUploadingFile(message.uploadId, file)
  else saveUploadingFile(message.uploadId, file)

  if (thumbnailFile && message.thumbnailUploadId)
    await saveUploadingFile(message.thumbnailUploadId, thumbnailFile)

  onAfterMessagesSentToChat()
  pendingUploadsStorage.insert(message)

  handlePendingUpload(message, file, thumbnailFile)
}

function convertPendingToSendPayload(
  message: PendingUpload,
  fileId: string,
  size: number,
) {
  const { attachment, type, repliedTo, body, id } = message
  return {
    attachment: { ...attachment, fileId, size },
    type,
    repliedTo,
    body,
    id,
  } as SendMessagePayloadWithPendingId
}

const processingPendingUploads = new Set<string>()
export const deleteProcessingPendingUploads = (key: string) => {
  if (processingPendingUploads.delete(key)) uploadsInQueueAction.invoke()
}

const uploadsInQueueAction = new Action<void>()

async function processUploadedMessage(
  message: PendingUpload,
  { fileId, size }: { size: number; fileId: string },
  resolver?: () => void,
) {
  if (Array.from(processingPendingUploads)[0] === message.uploadId) {
    deleteProcessingPendingUploads(message.uploadId)
    await sendMessage(message.roomId, [
      convertPendingToSendPayload(message, fileId, size),
    ])
    resolver?.()
  } else {
    if (resolver) return
    return new Promise<void>(resolve => {
      const deleteAction = uploadsInQueueAction.add(() => {
        processUploadedMessage(message, { fileId, size }, () => {
          deleteAction()
          resolve()
        })
      })
    })
  }
}

export function getConversion(
  message: Pick<PendingUpload, 'type'>,
  height?: number,
) {
  switch (message.type) {
    case 'VOICE':
      return { type: 'audio' } as const
    case 'VIDEO':
      return {
        type: 'video',
        options: { format: 'compress', height: height || 360 },
      } as const
    default:
      return undefined
  }
}

const uploadReporters = new Map<
  string,
  ReturnType<typeof createUploadProfiler>
>()

async function handleCompleteVideoPendingUpload(uploadId: string) {
  const message = pendingUploadsStorage.get(uploadId)
  const attachment = message.attachment as RawVideoMessage['attachment']
  const videoUploaded = attachment.fileId && message.conversionCompleted

  if (videoUploaded) {
    const uploadReporter = uploadReporters.get(uploadId)
    uploadReporter?.stop({ messageType: message.type, roomId: message.roomId })
    uploadReporters.delete(uploadId)
  }

  if (videoUploaded && attachment.thumbnailFileId) {
    await processUploadedMessage(message, {
      fileId: attachment.fileId,
      size: attachment.size,
    })
    await deleteUploadingFile(message.uploadId)
    await deleteUploadingFile(message.thumbnailUploadId!)
  }
}

async function updatePendingUpload(
  uploadId: string,
  update: (oldMessage: PendingUpload) => PendingUpload,
) {
  const tempMessage = pendingUploadsStorage.get(uploadId)
  const newMessage = update(tempMessage)
  await pendingUploadsStorage.update(newMessage)
}

export function handlePendingUpload(
  message: PendingUpload,
  file: File,
  thumbnailFile?: File,
  multipartMeta?: {
    uploadId: string
    fileId: string
  },
) {
  processingPendingUploads.add(message.uploadId)
  if (
    thumbnailFile &&
    message.thumbnailUploadId &&
    !(message.attachment as RawVideoMessage['attachment']).thumbnailFileId
  ) {
    let thumbnailUploadReporter = uploadReporters.get(message.thumbnailUploadId)
    if (!thumbnailUploadReporter) {
      thumbnailUploadReporter = createUploadProfiler(UploadSource.CHAT)
      uploadReporters.set(message.thumbnailUploadId, thumbnailUploadReporter)
    }
    thumbnailUploadReporter.startUpload(thumbnailFile.type, thumbnailFile.size)

    uploadFile({
      uploadId: message.thumbnailUploadId,
      file: thumbnailFile,
      type: 'infer',

      onComplete: async info => {
        await updatePendingUpload(message.uploadId, oldMessage =>
          R.assocPath(
            ['attachment', 'thumbnailFileId'],
            info.fileId,
            oldMessage,
          ),
        )
        thumbnailUploadReporter?.stop({
          roomId: message.roomId,
          messageType: message.type,
        })
        uploadReporters.delete(message.thumbnailUploadId!)
        handleCompleteVideoPendingUpload(message.uploadId)
      },
    })
  }

  let uploadReporter = uploadReporters.get(message.uploadId)
  if (!uploadReporter) {
    uploadReporter = createUploadProfiler(UploadSource.CHAT)
    uploadReporters.set(message.uploadId, uploadReporter)
  }

  uploadReporter.startUpload(file.type, file.size)

  uploadFile({
    uploadId: message.uploadId,
    file,
    type: 'infer',
    async onConversionProgress({ percent }) {
      if (message.thumbnailUploadId && percent === 100) {
        await updatePendingUpload(message.uploadId, oldMessage =>
          R.assoc('conversionCompleted', true, oldMessage),
        )
        handleCompleteVideoPendingUpload(message.uploadId)
      }
    },
    multipart: multipartMeta,
    onMultipartMetaIsReady: (multipartUploadId, fileId) => {
      updateUploadingFile(message.uploadId, {
        multipart: {
          uploadId: multipartUploadId,
          fileId,
        },
      })
    },
    onComplete: async info => {
      if (message.thumbnailUploadId) {
        await updatePendingUpload(message.uploadId, oldMessage =>
          R.assoc(
            'attachment',
            {
              ...oldMessage.attachment,
              fileId: info.fileId,
              size: info.size,
            },
            oldMessage,
          ),
        )

        handleCompleteVideoPendingUpload(message.uploadId)
        return
      }
      uploadReporter?.stop({
        roomId: message.roomId,
        messageType: message.type,
      })
      uploadReporters.delete(message.uploadId)
      await processUploadedMessage(message, info)
      await deleteUploadingFile(message.uploadId)
    },
    onProgress(percent) {
      if (percent === 100 && message.thumbnailUploadId)
        uploadReporter?.startConversion()

      const action =
        messageTypeToSendingMessageAction[message.type as MessageType]
      if (action) onSendingMessageAction(message.roomId, action)
    },
    convert: getConversion(
      message,
      (message.attachment as PendingUploadVideoAttachment).compressionHeight,
    ),
    queue: message.roomId,
  })
}

export async function syncPendingUploads() {
  await Promise.all(
    pendingUploadsStorage.getAll().map(async message => {
      if (processingPendingUploads.has(message.uploadId)) return
      const { file, multipart } =
        (await getUploadingFile(message.uploadId)) || {}

      if (!file) return
      if (message.thumbnailUploadId) {
        const { file: thumbnailFile } =
          (await getUploadingFile(message.thumbnailUploadId)) || {}
        if (thumbnailFile)
          handlePendingUpload(message, file, thumbnailFile, multipart)
      } else handlePendingUpload(message, file, undefined, multipart)
    }),
  )
}
